/* LEVEL 3 — 門診：只是沒睡好的孕婦（severe pre-eclampsia → eclampsia if missed） */
import {
  C,
  type Cell,
  chat,
  type ChoiceItem,
  clearWait,
  clock,
  closeDialog,
  closeZoom,
  type Ctx,
  dither,
  doc,
  ent,
  eventBanner,
  finding,
  focusInput,
  type Level,
  leaveBy,
  me,
  mkEnt,
  note,
  obox,
  openZoom,
  pell,
  pline,
  playEnding,
  portraitCanvas,
  post,
  R,
  rebuildDyn,
  session,
  sessionGuard,
  spend,
  strList,
  tr,
  walkEnt,
  ask,
  addWait,
  zone,
  type ZoomCfg,
} from "../../engine";

const FACTS = {
  headache: tr(
    "頭痛兩天，前額脹痛，吃普拿疼沒用",
    "Headache for two days, a pressing frontal ache; acetaminophen didn't help",
  ),
  vision: tr("看東西有閃光、霧霧的", "Seeing flashing lights, things look hazy"),
  epigastric: tr(
    "上腹／胃的地方悶痛，以為是胃食道逆流",
    "Dull ache in the upper belly / stomach area; she thought it was acid reflux",
  ),
  vomit: tr("今天早上吐了一次", "Vomited once this morning"),
  edema: tr("腳腫到鞋子穿不下，戒指拔不下來", "Feet so swollen her shoes won't fit; can't get her ring off"),
  fm: tr("胎動正常", "Fetal movement normal"),
  ctx: tr("沒有肚子緊、沒有出血、沒有破水", "No tightening, no bleeding, no leaking fluid"),
  urine: tr("尿量好像變少", "Seems to be peeing less"),
  reluctant: tr(
    "不想住院，週末要辦 baby shower，工作也還沒交接",
    "Doesn't want to be admitted: baby shower this weekend, and she hasn't handed off her work yet",
  ),
  hidden: tr(
    "她叫先生不要講，怕被留下來住院",
    "She told her husband not to say anything, afraid she'll be kept in the hospital",
  ),
  ama_saw: tr(
    "阿嬤早上看到她在廁所吐、一直揉上腹",
    "Grandma saw her vomiting in the restroom this morning, rubbing her upper belly the whole time",
  ),
} as const;
type FactId = keyof typeof FACTS;
const isFact = (x: string): x is FactId => x in FACTS;

const ORD = {
  dip: tr("尿蛋白試紙", "Urine protein dipstick"),
  labs: tr("抽血（CBC、肝腎功能、LDH、尿酸）", "Labs (CBC, liver and renal function, LDH, uric acid)"),
  nst: "NST",
  rebp: tr("請護理師重量血壓", "Nurse rechecks BP"),
} as const;
type OrderId = keyof typeof ORD;
const isOrder = (x: string): x is OrderId => x in ORD;

interface Bp {
  t: number;
  sbp: number;
  dbp: number;
  hr: number;
}
interface Order {
  at: number;
  ready: number;
  done: boolean;
}
type PtLoc = "pre" | "nst" | "wait" | "away" | "gone";

interface Vars {
  bpSeen: Bp[];
  printRead: boolean;
  emrRead: boolean;
  scaleRead: boolean;
  orders: Partial<Record<OrderId, Order>>;
  revealed: Set<FactId>;
  ptLoc: PtLoc;
  /** Orders requested while the patient was away. */
  queued: OrderId[];
  ate: boolean;
  toldStay: boolean;
  accepted: boolean;
  acceptedAt: number | null;
  awayAt: number | null;
  /** Seized while away, brought back by security. */
  awayFit: boolean;
  peeBack: { t: number; to: Cell } | null;
  seizureAt: number | null;
  seizing: boolean;
  seizure2At: number | null;
  sz2: boolean;
  helpCalled: boolean;
  mg: boolean;
  seizureActs: string[];
  vsTalked: boolean;
  vsComing: number | null;
  endAt: number | null;
  gone: boolean;
  knocked: boolean;
}

const S = session<Vars>();
const DOOR: Cell = [10, 11];

/** Finding name for leg edema; the close-up checks for it to draw swollen ankles. */
const F_EDEMA = tr("水腫", "Edema");

/* ---------- vitals & knowledge ---------- */

const bp = (): Omit<Bp, "t"> => ({ sbp: Math.round(160 + 0.15 * S.t), dbp: Math.round(106 + 0.1 * S.t), hr: 92 });

function known(): string {
  const out: string[] = [];
  const bps = [
    ...(S.printRead ? [tr("10:05 分診 158/104（自動血壓計）", "10:05 triage 158/104 (automated BP machine)")] : []),
    ...S.bpSeen.map((b) => `${clock(b.t)} ${b.sbp}/${b.dbp}`),
  ];
  out.push(tr(`知道的血壓：${bps.join("；") || "沒有"}`, `BPs known: ${bps.join("; ") || "none"}`));
  out.push(
    tr(
      `看過產檢紀錄：${S.emrRead ? "是（28 週 118/76、30 週 128/82、32 週 134/86；兩週前體重 64.0 kg）" : "否"}`,
      `Prenatal record reviewed: ${S.emrRead ? "yes (28 wk 118/76, 30 wk 128/82, 32 wk 134/86; weight two weeks ago 64.0 kg)" : "no"}`,
    ),
  );
  out.push(
    tr(
      `體重：${S.scaleRead ? "今天 67.4 kg（兩週增加 3.4 kg）" : "沒看"}`,
      `Weight: ${S.scaleRead ? "67.4 kg today (up 3.4 kg in two weeks)" : "not checked"}`,
    ),
  );
  out.push(
    tr(
      `檢查發現：${S.findings.map((f) => `${clock(f.t)} ${f.name}：${f.text}`).join("；") || "無"}`,
      `Exam findings: ${S.findings.map((f) => `${clock(f.t)} ${f.name}: ${f.text}`).join("; ") || "none"}`,
    ),
  );
  out.push(
    tr(
      `已開檢查：${
        Object.entries(S.orders)
          .map(([k, o]) => ORD[k as OrderId] + (o.done ? "（已出）" : "（等待中）"))
          .join("、") || "無"
      }`,
      `Tests ordered: ${
        Object.entries(S.orders)
          .map(([k, o]) => ORD[k as OrderId] + (o.done ? " (resulted)" : " (pending)"))
          .join(", ") || "none"
      }`,
    ),
  );
  out.push(
    tr(
      `問出的病史：${[...S.revealed].map((id) => FACTS[id]).join("；") || "無"}`,
      `History obtained: ${[...S.revealed].map((id) => FACTS[id]).join("; ") || "none"}`,
    ),
  );
  if (S.seizureAt !== null)
    out.push(
      tr(
        `抽搐：${clock(S.seizureAt)} 發生；clerk 的處置：${S.seizureActs.join("、") || "無"}`,
        `Seizure: at ${clock(S.seizureAt)}; clerk's management: ${S.seizureActs.join(", ") || "none"}`,
      ),
    );
  return out.join("\n");
}

const reveal = (ids: unknown): void => {
  for (const id of strList(ids)) if (isFact(id)) S.revealed.add(id);
};

/* ---------- orders & patient movement ---------- */

function order(id: OrderId): void {
  if (S.orders[id]) return;
  const needsPt = id === "dip" || id === "nst";
  if (needsPt && S.ptLoc === "away") {
    if (!S.queued.includes(id)) {
      S.queued.push(id);
      post(
        "nurse",
        "npc",
        "xiaowen",
        tr("她出去買東西了耶，等她回來我再弄。", "She went out to grab something. I’ll do it when she’s back."),
      );
    }
    return;
  }
  let eta = { dip: 5, labs: 25, nst: 20, rebp: 1 }[id];
  const e = ent("yijun");
  if (id === "dip") {
    if (S.seizureAt !== null) {
      eta = 8;
      post(
        "nurse",
        "npc",
        "xiaowen",
        tr(
          "她剛抽搐完，沒辦法自己去尿。我幫她放導尿管取尿。",
          "She just seized, she can’t go pee on her own. I’ll put in a catheter for the sample.",
        ),
      );
    } else if (!S.gone && !S.seizing) {
      const back: Cell = S.ptLoc === "pre" ? [1, 2] : S.ptLoc === "nst" ? [13, 2] : [9, 8];
      post("nurse", "npc", "xiaowen", tr("尿杯給她了。", "Gave her the urine cup."));
      post("pt", "narr", null, tr("怡君拿著尿杯去廁所。", "Yi-Chun takes the cup to the restroom."));
      walkEnt(e, [1, 9]);
      S.peeBack = { t: S.t + 3, to: back };
    }
  }
  if (id === "nst") {
    if (S.seizureAt !== null)
      post(
        "pt",
        "narr",
        null,
        tr(
          "小雯把胎兒監視器推到怡君身邊，直接接上。",
          "Hsiao-Wen wheels the fetal monitor over to Yi-Chun and hooks her up right there.",
        ),
      );
    else if (!S.seizing && !S.gone) {
      S.ptLoc = "nst";
      walkEnt(e, [13, 2]);
      e.onArrive = () => {
        e.face = "down";
      };
      post(
        "pt",
        "narr",
        null,
        tr("小雯帶怡君去胎兒監視器那邊。", "Hsiao-Wen takes Yi-Chun over to the fetal monitor."),
      );
    }
  }
  S.orders[id] = { at: S.t, ready: S.t + eta, done: false };
}

function leave(): void {
  if (S.ptLoc === "away" || S.gone || S.seizureAt !== null) return;
  S.ptLoc = "away";
  S.awayAt = S.t;
  clearWait("eat");
  closeZoom();
  post(
    "pt",
    "narr",
    null,
    tr(
      "怡君拿起包包：「那我很快回來喔！」往大廳走去。",
      "Yi-Chun grabs her bag. “I’ll be right back!” She heads for the lobby.",
    ),
  );
  const e = ent("yijun");
  e.face = null;
  leaveBy(e, DOOR, () => {
    if (S.dialog === "pt") closeDialog();
  });
}

function comeBack(): void {
  const e = ent("yijun");
  [e.x, e.y] = DOOR;
  e.fx = e.x;
  e.fy = e.y;
  e.hidden = false;
  walkEnt(e, [9, 8]);
  S.ptLoc = "wait";
  S.ate = true;
  post(
    "pt",
    "narr",
    null,
    tr(
      "怡君提著一個便當回來了，在候診區坐下開始吃。",
      "Yi-Chun comes back with a boxed lunch, sits down in the waiting area, and starts eating.",
    ),
  );
  const q = S.queued.slice();
  S.queued = [];
  q.forEach(order);
}

/* ---------- physical exam ---------- */

const ex = {
  bp: (): void => {
    const b = bp();
    S.bpSeen.push({ t: S.t, ...b });
    me("pt", tr("（量血壓）", "(Takes her BP)"));
    post("pt", "result", null, tr(`血壓 ${b.sbp}/${b.dbp}，HR ${b.hr}`, `BP ${b.sbp}/${b.dbp}, HR ${b.hr}`));
    spend(2);
  },
  find: (label: string, name: string, txt: string, cost = 1): void => {
    if (S.findings.some((f) => f.name === name)) {
      note(tr(`剛剛檢查過了：${txt}`, `Already checked: ${txt}`), "pt");
      return;
    }
    me("pt", tr(`（${label}）`, `(${label})`));
    finding(name, txt);
    post("pt", "narr", null, txt);
    spend(cost);
  },
  legs: () =>
    ex.find(
      tr("看下肢", "Checks her legs"),
      F_EDEMA,
      tr(
        "雙下肢 2+ 凹陷性水腫，腳背也腫，戒指緊到陷進肉裡。",
        "2+ pitting edema in both legs, the tops of her feet are swollen too, and her ring is cutting into her finger.",
      ),
    ),
  epi: () =>
    ex.find(
      tr("壓上腹部", "Presses on her upper abdomen"),
      tr("上腹", "Epigastrium"),
      tr(
        "按到右上腹和上腹正中，怡君皺眉往後縮：「那邊會痛。」",
        "When you press the RUQ and epigastrium, Yi-Chun winces and pulls back: “That hurts.”",
      ),
    ),
  reflex: () =>
    ex.find(
      tr("敲深部肌腱反射", "Checks deep tendon reflexes"),
      tr("反射", "Reflexes"),
      tr("膝反射 3+，踝部有三下 clonus。", "Knee reflexes 3+, three beats of ankle clonus."),
      2,
    ),
  fhr: () =>
    ex.find(
      tr("聽胎心音", "Listens to the fetal heart"),
      tr("胎心", "FHR"),
      tr("Doppler 胎心音 145 bpm，規則。", "Doppler FHR 145 bpm, regular."),
    ),
};

/* ---------- eclampsia ---------- */

function seize(): void {
  S.seizureAt = S.t;
  S.seizing = true;
  const e = ent("yijun");
  e.fx2 = "shake";
  e.path = [];
  S.peeBack = null;
  if (S.ptLoc === "away") {
    e.x = 9;
    e.y = 10;
    e.fx = 9;
    e.fy = 10;
    e.hidden = false;
    e.onArrive = null;
    rebuildDyn();
    S.ptLoc = "wait";
    S.awayFit = true;
    post(
      "nurse",
      "alert",
      "xiaowen",
      tr(
        "醫師！3 號在大廳便利商店門口抽筋，警衛用輪椅把她推回來了！",
        "Doctor! Number 3 had a fit outside the convenience store in the lobby. Security just wheeled her back!",
      ),
    );
  }
  const where = S.awayFit
    ? tr("大廳便利商店門口", "Outside the lobby store")
    : ((
        {
          pre: tr("預診室", "Pre-exam room"),
          nst: tr("胎兒監視器旁", "By the fetal monitor"),
          wait: tr("候診區", "Waiting area"),
        } as Partial<Record<PtLoc, string>>
      )[S.ptLoc] ?? tr("候診區", "Waiting area"));
  eventBanner(tr("子癲癇發作", "Eclamptic seizure"), where, true);
  post(
    "pt",
    "alert",
    "ama",
    tr(`（${where}）哎呀！她在抽筋！快來人啊！`, `(${where}) Aiya! She’s having a fit! Somebody come quick!`),
  );
  addWait({
    id: "sz",
    text: tr("怡君抽搐中！", "Yi-Chun is seizing!"),
    urgent: true,
    icon: tr("！", "!"),
    clearOn: "manual",
  });
  S.seizure2At = S.t + 12;
  closeZoom();
}

function helpArrives(noteText?: string): void {
  const n = ent("xiaowen");
  const pt = ent("yijun");
  walkEnt(n, [pt.x, pt.y + 1 <= 10 ? pt.y + 1 : pt.y - 1]);
  post(
    "pt",
    "npc",
    "xiaowen",
    noteText
      ? tr(`${noteText} 我推急救車過來了！`, `${noteText} I’ve got the crash cart!`)
      : tr("來了！急救車推過來了！我去叫主任！", "Coming! Crash cart’s here! I’ll get Dr. Lin!"),
  );
  S.vsComing ??= S.t + 3;
}

/* ---------- endings ---------- */

function endA(): void {
  void playEnding(
    "A",
    tr("及時收治", "Admitted in Time"),
    [
      { who: "vs", text: tr("下一號。", "Next.") },
      {
        who: "ama",
        text: tr("少年仔，你剛剛很認真喔。來，吃糖。", "Young one, you worked hard back there. Here, have a candy."),
      },
      {
        text: tr(
          "候診螢幕跳到下一號。你的白袍口袋裡多了一顆牛奶糖。",
          "The waiting-room screen flips to the next number. There’s a milk candy in your white coat pocket now.",
        ),
      },
    ],
    () =>
      tr(
        `怡君在 ${clock(S.acceptedAt ?? S.t)} 被送進產房，沒有在門診抽搐。`,
        `Yi-Chun went up to L&D at ${clock(S.acceptedAt ?? S.t)} without seizing in clinic.`,
      ),
  );
}

function endB(): void {
  const acts = S.seizureActs;
  void playEnding(
    "B",
    tr("門診抽搐", "Seizure in Clinic"),
    [
      {
        text: tr("推床到了。大家一起把怡君抬上床。", "The gurney arrives. Everyone lifts Yi-Chun onto it together."),
      },
      {
        who: "vs",
        text: tr("Mg 上了，送產房。你跟著來。", "Mg is running. Take her to L&D. You come with us."),
        fn: () => {
          leaveBy(ent("yijun"), DOOR);
          leaveBy(ent("xiaowen"), DOOR);
        },
      },
      { who: "husband", text: tr("老婆！等等我！", "Honey! Wait for me!"), fn: () => leaveBy(ent("husband"), DOOR) },
      {
        who: "ama",
        text: acts.some((a) => a.includes(tr("壓舌板", "tongue depressor")) || a.includes(tr("壓住", "Hold down")))
          ? tr("阿彌陀佛……剛剛那個醫生是不是被護士罵？", "Amitabha… did that doctor just get told off by the nurse?")
          : tr("阿彌陀佛……", "Amitabha…"),
      },
    ],
    () =>
      tr(
        `怡君在 ${clock(S.seizureAt ?? S.t)} 於門診發生子癲癇。你的處置：${acts.join("、") || "沒有做任何處置"}。`,
        `Yi-Chun had an eclamptic seizure in clinic at ${clock(S.seizureAt ?? S.t)}. Your management: ${acts.join(", ") || "nothing at all"}.`,
      ),
  );
}

/* ---------- close-up ---------- */

function drawPt(c: Ctx, now: number): void {
  R(c, "#e3eaf6", 0, 0, 192, 78);
  for (let x = 0; x < 192; x += 24) R(c, "#d6dff0", x, 0, 1, 78);
  R(c, "#a9b4cf", 0, 76, 192, 3);
  R(c, "#ccd6ea", 0, 79, 192, 29);
  for (let x = 0; x < 192; x += 16) R(c, "#bcc8e0", x, 79, 1, 29);
  dither(c, 0, 96, 192, 12, "#b4c0da", 0.4);
  obox(c, "#b98552", 6, 40, 44, 5);
  R(c, "#8e6238", 6, 45, 44, 2);
  R(c, "#6b4a34", 8, 47, 3, 28);
  R(c, "#6b4a34", 45, 47, 3, 28);
  obox(c, "#20263a", 14, 22, 24, 16);
  R(c, "#5f8fb8", 16, 24, 20, 12);
  R(c, "#9fd0ef", 17, 25, 6, 2);
  R(c, "#3a3f52", 24, 38, 4, 2);
  obox(c, "#4a6a9a", 70, 22, 52, 44);
  R(c, "#5f86bf", 72, 24, 48, 4);
  obox(c, "#5f86bf", 66, 64, 60, 8);
  R(c, "#3a3f52", 70, 73, 3, 20);
  R(c, "#3a3f52", 119, 73, 3, 20);
  const skinS = "#ddb5a2";
  const skin = "#f4dccd";
  const dress = "#e8a0b8";
  const dressS = "#c97f99";
  const line = "#1b1426";
  obox(c, skin, 64, 46, 10, 22);
  R(c, skinS, 64, 62, 10, 6);
  obox(c, skin, 118, 46, 10, 22);
  R(c, skinS, 118, 62, 10, 6);
  if (S.bpSeen.length) {
    obox(c, "#3b5fa8", 63, 48, 12, 9);
    R(c, "#5f86bf", 64, 49, 10, 2);
    pline(c, 68, 57, 60, 76, "#20263a", 1);
  }
  obox(c, dress, 76, 48, 40, 22);
  pell(c, 96, 66, 22, 12, line);
  pell(c, 96, 66, 21, 11, dress);
  pell(c, 92, 62, 9, 4, "#f2b8cc");
  R(c, dressS, 76, 48, 40, 2);
  obox(c, skin, 82, 76, 12, 16);
  obox(c, skin, 98, 76, 12, 16);
  R(c, skinS, 82, 76, 12, 2);
  R(c, skinS, 98, 76, 12, 2);
  const ed = S.findings.some((f) => f.name === F_EDEMA);
  const ankle = ed ? "#efc2b0" : skin;
  obox(c, ankle, ed ? 80 : 82, 90, ed ? 16 : 12, 7);
  obox(c, ankle, ed ? 96 : 98, 90, ed ? 16 : 12, 7);
  obox(c, "#2a2230", 80, 97, 15, 4);
  obox(c, "#2a2230", 97, 97, 15, 4);
  c.drawImage(portraitCanvas("yijun"), 72, 2, 48, 48);
  if (Math.floor(now / 1600) % 5 === 0) {
    R(c, "#3a2a22", 84, 24, 6, 3);
    R(c, "#3a2a22", 102, 24, 6, 3);
  }
}

function ptZoom(): ZoomCfg | null {
  if (S.seizing || S.seizureAt !== null || S.gone) return null;
  return {
    title: tr("怡君", "Yi-Chun"),
    sub: tr("點身體的部位來檢查，點臉跟她說話", "Tap a body part to examine it, tap her face to talk"),
    draw: drawPt,
    hot: [
      {
        x: 78,
        y: 4,
        w: 36,
        h: 34,
        label: tr("臉（跟她說話）", "Face (talk to her)"),
        fn: () => {
          closeZoom();
          focusInput();
        },
      },
      { x: 62, y: 44, w: 14, h: 26, label: tr("手臂（量血壓）", "Arm (BP)"), fn: ex.bp },
      { x: 82, y: 48, w: 28, h: 8, label: tr("上腹部（按壓）", "Upper abdomen (press)"), fn: ex.epi },
      { x: 76, y: 57, w: 40, h: 18, label: tr("肚子（聽胎心音）", "Belly (fetal heart)"), fn: ex.fhr },
      { x: 80, y: 76, w: 32, h: 12, label: tr("膝蓋（敲反射）", "Knee (reflexes)"), fn: ex.reflex },
      { x: 78, y: 88, w: 36, h: 14, label: tr("腳踝（看水腫）", "Ankle (edema)"), fn: ex.legs },
    ],
  };
}

/* ---------- dialogs ---------- */

function seizureChoices(): ChoiceItem[] {
  const act = (label: string, id: string, txt: string, wrong = false) =>
    C(
      label,
      () => {
        if (S.seizureActs.includes(label)) return;
        S.seizureActs.push(label);
        me("pt", tr(`（${label}）`, `(${label})`));
        post("pt", wrong ? "alert" : "narr", wrong ? "xiaowen" : null, txt);
        if (id === "help" && !S.helpCalled) {
          S.helpCalled = true;
          helpArrives();
        }
        if (id === "mg" && S.helpCalled) S.mg = true;
      },
      S.seizureActs.includes(label),
    );
  return [
    tr("抽搐處置", "Seizure management"),
    act(
      tr("讓她側躺、護住頭", "Turn her on her side, protect her head"),
      "side",
      tr(
        "你把怡君翻成左側躺，墊住她的頭，移開旁邊的椅子。",
        "You roll Yi-Chun onto her left side, cushion her head, and move the chairs out of the way.",
      ),
    ),
    act(
      tr("大聲叫人、按急救鈴", "Shout for help, hit the code bell"),
      "help",
      tr("你大喊叫人。", "You shout for help."),
    ),
    act(
      tr("給氧氣", "Give oxygen"),
      "o2",
      S.helpCalled
        ? tr("小雯把氧氣面罩給你，你幫她戴上。", "Hsiao-Wen hands you an oxygen mask and you put it on her.")
        : tr(
            "氧氣在急救車上，還沒有人推過來。",
            "The oxygen is on the crash cart, and nobody has brought it over yet.",
          ),
    ),
    act(
      tr("請準備 MgSO4 4 g IV", "Call for MgSO4 4 g IV"),
      "mg",
      S.helpCalled
        ? tr("小雯：「好，Mg 4 克準備中，先打 IV！」", "Hsiao-Wen: “OK, drawing up 4 g of Mg. IV first!”")
        : tr("你喊著要 MgSO4，但旁邊還沒有護理師。", "You shout for MgSO4, but there’s no nurse nearby yet."),
    ),
    act(
      tr("記下抽搐開始時間", "Note the seizure start time"),
      "time",
      tr(
        `你看了一下錶：${clock(S.seizureAt ?? S.t)} 開始。`,
        `You check your watch: it started at ${clock(S.seizureAt ?? S.t)}.`,
      ),
    ),
    act(
      tr("把壓舌板塞進嘴裡防咬舌", "Put a tongue depressor in her mouth"),
      "stick",
      tr("不要塞東西進去！會斷牙、會嗆到！", "Don’t put anything in her mouth! She’ll break teeth or choke!"),
      true,
    ),
    act(
      tr("壓住她的手腳", "Hold down her arms and legs"),
      "hold",
      tr(
        "不要壓她手腳，會骨折！讓她側躺就好！",
        "Don’t hold her down, you’ll break something! Just keep her on her side!",
      ),
      true,
    ),
    "sep",
    C(tr("量血壓", "Check BP"), ex.bp),
  ];
}

const dialogs: Level["dialogs"] = {
  pt: {
    title: tr("怡君", "Yi-Chun"),
    sub: () =>
      S.seizing
        ? tr("抽搐中", "Seizing")
        : S.seizureAt !== null
          ? tr("抽搐後", "Post-ictal")
          : tr("34 週，第一胎", "34 weeks, first baby"),
    pics: ["yijun"],
    ch: "pt",
    talk: () => !S.seizing && !S.gone,
    ph: tr("對怡君說…", "Say something to Yi-Chun…"),
    choices() {
      if (S.gone) return [];
      if (S.seizing || S.seizureAt !== null) return seizureChoices();
      return [
        C(tr("特寫", "Close-up"), () => {
          const z = ptZoom();
          if (z) openZoom({ dlg: "pt", ...z });
        }),
        C(tr("幫她量血壓", "Take her BP"), ex.bp),
        C(tr("看下肢", "Check legs"), ex.legs),
        C(tr("壓上腹部", "Press upper abdomen"), ex.epi),
        C(tr("敲深部肌腱反射", "Check reflexes"), ex.reflex),
        C(tr("聽胎心音", "Fetal heart"), ex.fhr),
      ];
    },
    async onTalk(text) {
      const loc =
        (
          {
            pre: tr("預診室", "the pre-exam room"),
            nst: tr("胎兒監視器旁邊", "next to the fetal monitor"),
            wait: tr("候診區", "the waiting area"),
            away: tr("大廳", "the lobby"),
          } as Partial<Record<PtLoc, string>>
        )[S.ptLoc] ?? tr("候診區", "the waiting area");
      await chat(text, {
        ch: "pt",
        think: "yijun",
        speakers: { yijun: tr("怡君本人", "Yi-Chun herself") },
        body: tr(
          `你在醫學教育遊戲中扮演產婦「林怡君」。玩家是婦產科見習醫學生（clerk），在門診幫你預診。
怡君：30 歲上班族，第一胎 34 週。個性開朗、很能忍，習慣把不舒服說得很輕（「應該只是沒睡好」「應該是胃食道逆流」），很怕被留下來住院（週末要辦 baby shower，工作還沒交接）。被直接問到的症狀不會說謊，但會淡化。
【隱藏真相，不能說出】子癲前症合併嚴重特徵，正往 HELLP 走。
【可揭露的事實】只有被問到相關問題才說，括號是 id：
${Object.entries(FACTS)
  .filter(([k]) => k !== "hidden" && k !== "ama_saw")
  .map(([k, v]) => `- (${k}) ${v}`)
  .join("\n")}
vision 只有被具體問到「眼睛／視力／看東西」才說。epigastric 只有問到肚子上面或胃才說。
【行動】act 只能是 none、leave、stay。clerk 同意她現在先離開去吃東西、買東西時用 leave（她會真的走掉，大概八分鐘後回來）；clerk 請她留下、先不要吃東西時用 stay；其他情況 none。她剛抽搐過或已經被收住院時一律 none。
【狀態】時間 ${clock()}；她在${loc}。${S.ate ? "她剛剛去大廳買了便當，已經吃了。" : ""}${S.seizureAt !== null ? "她剛抽搐過，很累、意識還有點模糊，講話很短。" : ""}已揭露：${[...S.revealed].join(",") || "無"}`,
          `In a medical education game you are playing the pregnant patient "Lin Yi-Chun". The player is an OB/GYN clerk (medical student on clerkship) doing your pre-exam intake in the clinic.
Yi-Chun: 30-year-old office worker, first pregnancy, 34 weeks. Cheerful and very stoic; she habitually plays her discomfort down ("I probably just didn't sleep well", "it's probably acid reflux") and is very afraid of being admitted (she has a baby shower this weekend and hasn't handed off her work yet). She will not lie about symptoms she is asked about directly, but she will minimize them.
[HIDDEN TRUTH, NEVER SAY IT] Preeclampsia with severe features, progressing toward HELLP.
[DISCLOSABLE FACTS] Share each only when asked a related question; the id is in parentheses:
${Object.entries(FACTS)
  .filter(([k]) => k !== "hidden" && k !== "ama_saw")
  .map(([k, v]) => `- (${k}) ${v}`)
  .join("\n")}
Only mention vision if specifically asked about "eyes / vision / seeing". Only mention epigastric if asked about the upper belly or the stomach.
[ACTIONS] act must be one of none, leave, stay. Use leave when the clerk agrees she can step out now to eat or buy something (she will actually walk away and come back in about eight minutes); use stay when the clerk asks her to stay and not eat for now; otherwise none. Always none if she has just had a seizure or has already been admitted.
[STATE] Time ${clock()}; she is in ${loc}. ${S.ate ? "She just went to the lobby, bought a boxed lunch, and has already eaten it. " : ""}${S.seizureAt !== null ? "She has just had a seizure: she is exhausted, still a little confused, and speaks in very short phrases. " : ""}Revealed: ${[...S.revealed].join(",") || "none"}`,
        ),
        extra: `"revealed":[],"act":"none"`,
        after(r) {
          reveal(r.revealed);
          if (r.act === "leave") leave();
          else if (r.act === "stay") {
            S.toldStay = true;
            clearWait("eat");
          }
        },
      });
    },
  },
  husband: {
    title: tr("志明", "Chih-Ming"),
    sub: tr("怡君的先生，在候診區", "Yi-Chun’s husband, in the waiting area"),
    pics: ["husband"],
    ch: "husband",
    talk: true,
    ph: tr("對志明說…", "Say something to Chih-Ming…"),
    async onTalk(text) {
      await chat(text, {
        ch: "husband",
        think: "husband",
        speakers: { husband: tr("志明", "Chih-Ming") },
        body: tr(
          `你在醫學教育遊戲中扮演「志明」，32 歲工程師，林怡君的先生，在門診候診區等太太。玩家是婦產科見習醫學生（clerk）。
志明老實、有點緊張。太太叫他不要講太多，怕被留下來住院，但醫療人員認真問的話他會說，而且會比太太講得更具體。
【他知道的事】括號是 id：
- (headache) 她頭痛兩天，吃普拿疼沒用
- (vision) 她昨天說看手機有一閃一閃的光
- (edema) 腳腫到鞋子穿不下，結婚戒指拔不下來
- (epigastric) 昨晚胃痛睡不著，吃了胃藥沒用
- (hidden) 她叫我不要講，怕被留下來住院
- (reluctant) 週末要辦 baby shower
只有 clerk 問到相關（或問「最近有沒有什麼不舒服」）時才說。不要說出診斷。
【狀態】時間 ${clock()}。${S.seizureAt !== null ? "他剛知道太太抽搐，非常驚慌。" : ""}`,
          `In a medical education game you are playing "Chih-Ming", a 32-year-old engineer, Lin Yi-Chun's husband, waiting for his wife in the clinic waiting area. The player is an OB/GYN clerk (medical student on clerkship).
Chih-Ming is honest and a little nervous. His wife told him not to say too much because she's afraid of being admitted, but if someone on the medical team asks him seriously he will tell them, and he'll be more specific than his wife.
[WHAT HE KNOWS] The id is in parentheses:
- (headache) She's had a headache for two days; acetaminophen didn't help
- (vision) Yesterday she said she was seeing flashing lights when she looked at her phone
- (edema) Her feet are so swollen her shoes don't fit, and she can't get her wedding ring off
- (epigastric) Last night her stomach hurt so much she couldn't sleep; antacids didn't help
- (hidden) She told me not to say anything, she's afraid of being kept in the hospital
- (reluctant) There's a baby shower this weekend
Only say these when the clerk asks about something related (or asks "has anything been bothering her lately?"). Do not state a diagnosis.
[STATE] Time ${clock()}. ${S.seizureAt !== null ? "He has just found out his wife had a seizure and is panicking." : ""}`,
        ),
        extra: `"revealed":[]`,
        after: (r) => reveal(r.revealed),
      });
    },
  },
  ama: {
    title: tr("阿嬤", "Grandma"),
    sub: tr("陪媳婦來產檢", "Here with her daughter-in-law"),
    pics: ["ama"],
    ch: "ama",
    talk: true,
    ph: tr("跟阿嬤聊天…", "Chat with Grandma…"),
    async onTalk(text) {
      await chat(text, {
        ch: "ama",
        think: "ama",
        speakers: { ama: tr("阿嬤", "Grandma") },
        body: tr(
          `你在醫學教育遊戲中扮演候診區的「阿嬤」，72 歲，陪媳婦來產檢，講話夾一點台語（「啊」「歹勢」「乖孫」），很熱情、話很多，會塞糖果給年輕醫師，常常扯到孫子。
她早上在廁所看到隔壁那個大肚子的小姐（林怡君）在吐，吐完一直揉胸口下面（上腹），臉色很差，還說頭很痛。只要 clerk 跟她聊到怡君、或問她有沒有看到什麼，她就會講（id: ama_saw）。clerk 沒問，她會先聊別的。
不要說出任何醫學診斷。
【狀態】時間 ${clock()}。${S.seizureAt !== null ? "她剛看到怡君抽筋，嚇壞了。" : ""}`,
          `In a medical education game you are playing "Grandma" in the waiting area: 72 years old, here with her daughter-in-law for a prenatal visit. She speaks warm, folksy, rambling English with the odd Taiwanese exclamation ("aiya", "paiseh" for "sorry"), calls her grandson her "precious grandbaby", is very friendly and talkative, presses candy on young doctors, and keeps drifting onto her grandkids.
This morning in the restroom she saw the young lady with the big belly from the next seat over (Lin Yi-Chun) vomiting; afterwards the young lady kept rubbing just below her chest (the upper belly), looked terrible, and said her head hurt a lot. As soon as the clerk chats with her about Yi-Chun or asks whether she noticed anything, she tells them (id: ama_saw). If the clerk doesn't ask, she chats about other things first.
Do not state any medical diagnosis.
[STATE] Time ${clock()}. ${S.seizureAt !== null ? "She just saw Yi-Chun having a fit and is terrified." : ""}`,
        ),
        extra: `"revealed":[]`,
        after: (r) => reveal(r.revealed),
      });
    },
  },
  nurse: {
    title: tr("小雯", "Hsiao-Wen"),
    sub: tr("跟診護理師", "Clinic nurse"),
    pics: ["xiaowen"],
    ch: "nurse",
    talk: true,
    ph: tr("對小雯說，也可以口頭請她做事", "Talk to Hsiao-Wen, or ask her to do something"),
    choices() {
      return [
        C(
          tr("驗尿蛋白（試紙）", "Urine protein (dipstick)"),
          () => {
            me("nurse", tr("（驗尿蛋白）", "(Orders a urine protein dip)"));
            order("dip");
            spend(1);
          },
          !!S.orders.dip,
        ),
        C(
          tr("抽血：CBC、肝腎、LDH、尿酸（急件）", "Labs: CBC, LFTs, renal, LDH, uric acid (stat)"),
          () => {
            me("nurse", tr("（抽血急件）", "(Orders stat labs)"));
            order("labs");
            post("nurse", "npc", "xiaowen", tr("急件送了，大概二十五分鐘。", "Sent stat. About twenty-five minutes."));
            spend(1);
          },
          !!S.orders.labs,
        ),
        C(
          tr("安排 NST", "Set up NST"),
          () => {
            me("nurse", tr("（安排 NST）", "(Sets up an NST)"));
            order("nst");
            spend(1);
          },
          !!S.orders.nst,
        ),
        C(tr("請她重量一次血壓", "Ask her to recheck BP"), () => {
          me("nurse", tr("（請重量血壓）", "(Asks for a repeat BP)"));
          delete S.orders.rebp;
          order("rebp");
          spend(1);
        }),
      ];
    },
    async onTalk(text) {
      await chat(text, {
        ch: "nurse",
        think: "xiaowen",
        speakers: { xiaowen: tr("小雯", "Hsiao-Wen") },
        body: tr(
          `你在醫學教育遊戲中扮演門診跟診護理師「小雯」，28 歲，動作快、講話俐落，手上同時處理很多號碼。玩家是婦產科見習醫學生（clerk）。
她 10:05 用自動血壓計幫 3 號林怡君量了 158/104，印單夾在血壓計上，太忙沒有標註也沒提醒主任。被問到時會承認並有點不好意思。
【clerk 目前知道的】
${known()}
【可執行的事 id】dip 尿蛋白試紙；labs 抽血急件；nst 安排胎兒監視器；rebp 重量血壓
規則：clerk 明確要求的事，把 id 放進 orders。不要編造還沒出來的結果。不要說出診斷。`,
          `In a medical education game you are playing the clinic nurse "Hsiao-Wen": 28, fast-moving and crisp, juggling a lot of patient numbers at once. The player is an OB/GYN clerk (medical student on clerkship).
At 10:05 she took a BP on number 3, Lin Yi-Chun, with the automated machine: 158/104. The printout is clipped to the BP machine; she was too busy to flag it or tell Dr. Lin. If asked about it, she admits it and is a little embarrassed.
[WHAT THE CLERK KNOWS SO FAR]
${known()}
[ACTION IDS SHE CAN DO] dip urine protein dipstick; labs stat bloodwork; nst set up the fetal monitor; rebp recheck BP
Rules: for anything the clerk explicitly asks for, put the id in orders. Do not make up results that aren't back yet. Do not state a diagnosis.`,
        ),
        extra: `"orders":[]`,
        after(r) {
          for (const id of strList(r.orders))
            if (isOrder(id)) {
              if (id === "rebp") delete S.orders.rebp;
              order(id);
            }
        },
        cost: 1,
      });
    },
  },
  vs: {
    title: tr("林主任", "Dr. Lin"),
    sub: tr("診間，正在看別的病人", "Exam room, seeing another patient"),
    pics: ["vs"],
    ch: "vs",
    talk: true,
    ph: tr("跟林主任報告…", "Present to Dr. Lin…"),
    async onTalk(text) {
      S.vsTalked = true;
      const b = bp();
      await chat(text, {
        ch: "vs",
        think: "vs",
        tier: "default",
        speakers: { vs: tr("林主任", "Dr. Lin") },
        body: tr(
          `你在醫學教育遊戲中扮演婦產科主治「林主任」，正在看門診，今天 58 號，很趕。玩家是見習醫學生（clerk），進診間來報告預診的 3 號林怡君。
林主任話少，講重點，用問題逼學生思考，中英夾雜（「BP 多少？重量了沒？」「有沒有 severe feature？」）。學生講得好就簡短肯定並補一句；漏掉就追問。不直接說出診斷。
【真相】34 週初產婦，子癲前症合併嚴重特徵、正往 HELLP 走。實際血壓約 ${b.sbp}/${b.dbp}。合理處置：收住院、MgSO4 預防抽搐、降壓、抽血看 HELLP、評估生產時機。
【clerk 實際知道的】（講出以外的數據就追問「哪來的？」）
${known()}
【action】accept_plan：clerk 講出嚴重範圍血壓，並提出子癲前症（有嚴重特徵）的判斷與收住院或 MgSO4 等處置。你同意，簡短交代：收產房、Mg loading、降壓、抽血，並給一句評語。
dismiss：clerk 什麼都還沒評估就進來，叫他出去做完再進來。
continue：其他情況。
${S.seizureAt !== null ? "注意：病人剛剛已經在門診抽搐了。" : ""}`,
          `In a medical education game you are playing the OB/GYN attending "Dr. Lin", who is running clinic with 58 patients today and is very rushed. The player is a clerk (medical student on clerkship) who has come into the exam room to present number 3, Lin Yi-Chun, whom they pre-examined.
Dr. Lin says little and sticks to the point, using questions to make the student think, in clipped clinical shorthand ("BP? Did you recheck it?", "Any severe features?"). If the student does well, he gives brief approval and adds one point; if they miss something, he presses. He does not state the diagnosis outright.
[TRUTH] 34-week primigravida with preeclampsia with severe features, progressing toward HELLP. Actual BP about ${b.sbp}/${b.dbp}. Appropriate management: admit, MgSO4 for seizure prophylaxis, antihypertensives, labs for HELLP, assess timing of delivery.
[WHAT THE CLERK ACTUALLY KNOWS] (if they cite data beyond this, press them: "Where'd you get that?")
${known()}
[action] accept_plan: the clerk states a BP in the severe range and proposes preeclampsia (with severe features) together with admission, MgSO4, or similar management. You agree and give brief instructions: admit to L&D, Mg loading, BP control, labs, plus one line of feedback.
dismiss: the clerk comes in without having assessed anything; tell them to go out, finish, and come back.
continue: anything else.
${S.seizureAt !== null ? "Note: the patient has already had a seizure in clinic." : ""}`,
        ),
        extra: `"action":"continue"`,
        after(r) {
          const severe = S.printRead || S.bpSeen.some((x) => x.sbp >= 160 || x.dbp >= 110);
          if (r.action === "accept_plan" && severe && !S.accepted && S.seizureAt === null) {
            S.accepted = true;
            S.acceptedAt = S.t;
            S.mg = true;
            S.gone = true;
            S.ptLoc = "gone";
            eventBanner(tr("收住院", "Admitted"), tr("Mg loading・送產房", "Mg loading · to L&D"));
            const e = ent("yijun");
            post(
              "pt",
              "narr",
              null,
              tr(
                "小雯推著輪椅過來，扶怡君坐上去，往產房走。",
                "Hsiao-Wen brings over a wheelchair, helps Yi-Chun into it, and heads for L&D.",
              ),
            );
            walkEnt(ent("xiaowen"), [e.x, Math.min(10, e.y + 1)]);
            const alive = sessionGuard();
            setTimeout(() => {
              if (alive()) leaveBy(e, DOOR, endA);
            }, 900);
          } else if (r.action === "accept_plan" && !severe) {
            post(
              "vs",
              "npc",
              "vs",
              tr("你連血壓都沒有，我怎麼收？", "You don’t even have a BP. How am I supposed to admit her?"),
            );
            S.mood.vs = (S.mood.vs ?? 0) + 1;
          }
          if (r.action === "dismiss") {
            post("vs", "narr", null, tr("林主任揮揮手要你出去。", "Dr. Lin waves you out."));
            S.mood.vs = (S.mood.vs ?? 0) + 1;
          }
        },
      });
    },
  },
  emr: {
    title: tr("產檢紀錄", "Prenatal record"),
    sub: tr("林怡君", "Lin Yi-Chun"),
    ch: "emr",
    doc: doc(() => {
      S.emrRead = true;
      return [
        [
          tr("基本資料", "Basics"),
          tr(
            "林怡君，30 歲，G1P0，目前 34+0 週。B 型 Rh(+)。",
            "Lin Yi-Chun, 30, G1P0, currently 34+0 weeks. Blood type B, Rh(+).",
          ),
        ],
        [
          tr("過去病史", "Past history"),
          tr(
            "兒童期氣喘，換季偶發作，平時用吸入劑。無高血壓、無糖尿病。",
            "Childhood asthma, occasional flares when the seasons change; uses an inhaler. No hypertension, no diabetes.",
          ),
        ],
        [
          tr("歷次產檢血壓", "Prenatal BPs"),
          tr(
            "28 週 118/76；30 週 128/82；32 週 134/86（尿蛋白 trace）。",
            "28 wk 118/76; 30 wk 128/82; 32 wk 134/86 (urine protein trace).",
          ),
        ],
        [tr("體重", "Weight"), tr("兩週前（32 週）64.0 kg。", "64.0 kg two weeks ago (32 wk).")],
        [
          tr("今日分診", "Today’s triage"),
          tr(
            "10:05 已量血壓與體重，結果在血壓計印單與體重計紀錄。",
            "BP and weight taken at 10:05; results are on the BP machine printout and the scale log.",
          ),
        ],
      ];
    }),
  },
  bp: {
    title: tr("自動血壓計", "BP machine"),
    sub: tr("候診區", "Waiting area"),
    ch: "bp",
    doc: doc(() => {
      S.printRead = true;
      return [
        [
          tr("印單（夾在機器上）", "Printout (clipped to the machine)"),
          tr(
            "10:05　林怡君　158 / 104 mmHg　HR 88。紙上沒有任何標註。",
            "10:05 · Lin Yi-Chun · 158 / 104 mmHg · HR 88. Nothing is written on it.",
          ),
        ],
        [
          tr("提醒", "Note"),
          tr(
            "要重量的話，可以請小雯，或自己在預診室手動量。",
            "To recheck, ask Hsiao-Wen, or take it manually yourself in the pre-exam room.",
          ),
        ],
      ];
    }),
  },
  scale: {
    title: tr("體重計", "Scale"),
    sub: tr("候診區", "Waiting area"),
    ch: "scale",
    doc: doc(() => {
      S.scaleRead = true;
      return [
        [tr("今日紀錄", "Today’s log"), tr("10:04　林怡君　67.4 kg", "10:04 · Lin Yi-Chun · 67.4 kg")],
        [
          tr("對照", "Compare"),
          tr("兩週前 64.0 kg（兩週增加 3.4 kg）", "64.0 kg two weeks ago (up 3.4 kg in two weeks)"),
        ],
      ];
    }),
  },
  nst: {
    title: tr("胎兒監視器", "Fetal monitor"),
    sub: tr("NST 室", "NST room"),
    ch: "nstc",
    doc: doc(() => {
      const o = S.orders.nst;
      return [
        [
          tr("狀態", "Status"),
          o
            ? o.done
              ? tr("NST：反應型，基線 145 bpm，沒有宮縮。", "NST: reactive, baseline 145 bpm, no contractions.")
              : tr(
                  `怡君正在做 NST，約 ${clock(o.ready)} 完成。`,
                  `Yi-Chun is on the NST, done around ${clock(o.ready)}.`,
                )
            : tr("目前沒有在用。可以請小雯安排。", "Not in use. Ask Hsiao-Wen to set one up."),
        ],
      ];
    }),
  },
};

/* ---------- level ---------- */

export const OPD: Level = {
  id: "opd",
  no: tr("三", "3"),
  short: tr("第三關　門診", "Level 3 · Clinic"),
  title: tr("門診：只是沒睡好的孕婦", "Clinic: Just a Bad Night’s Sleep"),
  place: tr("產檢門診", "Prenatal clinic"),
  blurb: tr(
    "34 週孕婦說最近頭有點痛，大概沒睡好。",
    "A woman 34 weeks pregnant says her head’s been aching a bit. Probably just poor sleep.",
  ),
  cast: ["yijun", "husband", "ama", "xiaowen", "vs"],
  intro: tr(
    [
      "週三上午 10:20，林主任的產檢門診，今天掛了 58 號。你負責先幫病人預診，問完再進診間跟主任報告。",
      "3 號林怡君，30 歲，第一胎，34 週，例行產檢。她已經坐在預診室等你。先生在外面候診區，旁邊還有一位很愛聊天的阿嬤。",
    ],
    [
      "Wednesday, 10:20. Dr. Lin’s prenatal clinic has 58 patients booked today. Your job is to pre-examine each patient first, then go into the exam room and present to Dr. Lin.",
      "Number 3 is Lin Yi-Chun: 30, first baby, 34 weeks, routine prenatal visit. She’s already waiting for you in the pre-exam room. Her husband is out in the waiting area, next to a very chatty grandma.",
    ],
  ),
  start: 10 * 60 + 20,
  maxT: 80,
  night: false,
  map: [
    "####################",
    "#aaaa#bbbbb#ccccccc#",
    "#aaaa#bbbbb#ccccccc#",
    "#aaaa#bbbbb#ccccccc#",
    "##a#####b#####c#####",
    "#wwwwwwwwwwwwwwwwww#",
    "#wwwwwwwwwwwwwwwwww#",
    "#wwwwwwwwwwwwwwwwww#",
    "#wwwwwwwwwwwwwwwwww#",
    "#wwwwwwwwwwwwwwwwww#",
    "#wwwwwwwwwwwwwwwwww#",
    "####################",
  ],
  zones: {
    a: ["#e3eaf6", "#ccd6ea", "#d6dff0"],
    b: ["#e3efe4", "#cadfcc", "#d6e7d8"],
    c: ["#f3e6ee", "#e2cdda", "#ead8e3"],
    w: ["#ece6d6", "#d9d0bb", "#e2dac7"],
  },
  windows: [
    [3, 0],
    [8, 0],
    [16, 0],
  ],
  exits: [DOOR],
  chName: {
    pt: tr("怡君", "Yi-Chun"),
    nurse: tr("小雯", "Hsiao-Wen"),
    vs: tr("林主任", "Dr. Lin"),
    husband: tr("志明", "Chih-Ming"),
    ama: tr("阿嬤", "Grandma"),
    sys: tr("門診", "Clinic"),
  },
  objects: [
    { id: "desk1", type: "desk", x: 2, y: 1, w: 2 },
    { id: "emr", type: "computer", x: 3, y: 1, label: tr("電腦：產檢紀錄", "Computer: prenatal record") },
    { id: "desk2", type: "desk", x: 7, y: 2, w: 2, label: tr("林主任的診桌", "Dr. Lin’s desk"), act: "vs" },
    { id: "exam", type: "exambed", x: 10, y: 1, h: 2 },
    { id: "rec1", type: "recliner", x: 13, y: 1 },
    { id: "nst", type: "nst", x: 14, y: 1, label: tr("胎兒監視器", "Fetal monitor") },
    { id: "rec2", type: "recliner", x: 16, y: 1 },
    { id: "nst2", type: "nst", x: 17, y: 1 },
    { id: "chairsA", type: "chairs", x: 2, y: 7, w: 7 },
    { id: "chairsB", type: "chairs", x: 2, y: 9, w: 7 },
    { id: "counter", type: "counter", x: 14, y: 7, w: 4, label: tr("跟診護理站", "Nurses’ station"), act: "nurse" },
    { id: "bp", type: "bp", x: 18, y: 8, label: tr("自動血壓計", "BP machine") },
    { id: "scale", type: "scale", x: 18, y: 9, label: tr("體重計", "Scale") },
    { id: "water", type: "water", x: 1, y: 5 },
    { id: "plant", type: "plant", x: 18, y: 10 },
    { id: "wc", type: "wcdoor", x: 0, y: 9, label: tr("廁所", "Restroom") },
  ],
  labels: {
    pt: tr("怡君", "Yi-Chun"),
    husband: tr("志明", "Chih-Ming"),
    ama: tr("阿嬤", "Grandma"),
    nurse: tr("小雯", "Hsiao-Wen"),
    vs: tr("林主任", "Dr. Lin"),
  },
  help: {
    pt: tr("問診、身體檢查（會打開特寫）", "History and exam (opens a close-up)"),
    emr: tr("歷次產檢的血壓、體重、過去病史", "Past prenatal BPs, weights, past history"),
    vs: tr("在診間看診，進去報告", "Seeing patients; go in to present"),
    nst: tr("胎兒監視器，看 NST 進度", "Fetal monitor: check NST progress"),
    nurse: tr("驗尿、抽血、排 NST、重量血壓", "Urine, labs, NST, repeat BP"),
    bp: tr("分診時量的血壓印單", "BP printout from triage"),
    scale: tr("今天的體重紀錄", "Today’s weight log"),
    wc: tr("清潔阿姨可能知道點什麼", "The cleaning lady might know something"),
    husband: tr("在候診區等太太", "Waiting for his wife"),
    ama: tr("很愛聊天的候診阿嬤", "A chatty grandma in the waiting area"),
  },
  helpRules: tr(
    [
      "你是預診的人：問完、檢查完，再進診間向林主任報告。",
      "林主任在看其他病人，進去之前先想好要講什麼。",
      "病人怎麼說是一回事，旁邊的人可能知道更多。",
    ],
    [
      "You do the pre-exam: take the history, examine her, then go into the exam room and present to Dr. Lin.",
      "Dr. Lin is seeing other patients. Know what you’re going to say before you go in.",
      "What the patient says is one thing. The people around her may know more.",
    ],
  ),
  player: [3, 3],
  ents: () => [
    mkEnt({ k: "yijun", id: "yijun", x: 1, y: 2, act: "pt", dir: "right", face: "right" }),
    mkEnt({ k: "vs", id: "vs", x: 8, y: 1, act: "vs" }),
    mkEnt({ k: "xiaowen", id: "xiaowen", x: 15, y: 6, act: "nurse" }),
    mkEnt({ k: "husband", id: "husband", x: 7, y: 9, act: "husband" }),
    mkEnt({ k: "ama", id: "ama", x: 4, y: 7, act: "ama" }),
  ],
  init: (): Vars => ({
    bpSeen: [],
    printRead: false,
    emrRead: false,
    scaleRead: false,
    orders: {},
    revealed: new Set(),
    ptLoc: "pre",
    queued: [],
    ate: false,
    toldStay: false,
    accepted: false,
    acceptedAt: null,
    awayAt: null,
    awayFit: false,
    peeBack: null,
    seizureAt: null,
    seizing: false,
    seizure2At: null,
    sz2: false,
    helpCalled: false,
    mg: false,
    seizureActs: [],
    vsTalked: false,
    vsComing: null,
    endAt: null,
    gone: false,
    knocked: false,
  }),
  dialogs,
  zooms: { pt: ptZoom },
  quick: {
    wc: () =>
      note(
        S.seizureAt === null
          ? tr(
              "廁所。清潔阿姨說早上有人在這裡吐，她剛拖完。",
              "Restroom. The cleaning lady says someone threw up in here this morning. She just finished mopping.",
            )
          : tr("廁所。", "Restroom."),
      ),
  },

  onStart: () =>
    eventBanner(
      tr("預診開始", "Pre-exam begins"),
      tr("林主任產檢門診・今天 58 號", "Dr. Lin’s prenatal clinic · 58 patients today"),
    ),
  onStep(x, y) {
    if (zone(x, y) === "b" && !S.knocked) {
      S.knocked = true;
      post("vs", "npc", "vs", tr("進來要先敲門。", "Knock before you come in."));
    }
  },
  tick(t) {
    if (t === 1)
      post(
        "nurse",
        "npc",
        "xiaowen",
        tr(
          "學弟，3 號林怡君先給你預診，問完跟主任報告。主任今天 58 號，動作快一點喔。",
          "Hey, number 3, Lin Yi-Chun, is yours to pre-exam. Present to Dr. Lin when you’re done. He’s got 58 today, so move it.",
        ),
      );
    if (t === 15 && !S.vsTalked)
      ask("vs", "vs", tr("3 號好了沒？", "Is number 3 ready yet?"), {
        id: "vsReport",
        text: tr("林主任在等你報告 3 號", "Dr. Lin is waiting for you to present #3"),
        dlg: "vs",
      });
    if (S.peeBack && t >= S.peeBack.t && !S.seizing && S.seizureAt === null && S.ptLoc !== "away") {
      const pb = S.peeBack;
      S.peeBack = null;
      walkEnt(ent("yijun"), pb.to);
      post(
        "pt",
        "narr",
        null,
        tr("怡君把尿杯交給小雯，走回來。", "Yi-Chun hands the cup to Hsiao-Wen and walks back."),
      );
    }
    if (S.ptLoc === "away" && S.awayAt !== null && t === S.awayAt + 8 && S.seizureAt === null) comeBack();
    if (t === 24 && !S.accepted && !S.seizing && !S.gone && S.ptLoc !== "away")
      ask(
        "pt",
        "yijun",
        tr(
          "醫生，我可以先去買個東西吃嗎？我早上吐完到現在都沒吃。",
          "Doctor, can I go grab something to eat first? I haven’t eaten anything since I threw up this morning.",
        ),
        {
          id: "eat",
          text: tr("怡君問能不能去吃東西", "Yi-Chun asks if she can go eat"),
          dlg: "pt",
        },
      );
    for (const [k, o] of Object.entries(S.orders) as [OrderId, Order][]) {
      if (o.done || t < o.ready) continue;
      o.done = true;
      if (k === "dip") {
        finding(tr("尿蛋白", "Urine protein"), tr("試紙 3+", "Dipstick 3+"));
        post("nurse", "result", "xiaowen", tr("尿蛋白試紙：3+。", "Urine protein dip: 3+."));
      }
      if (k === "labs") {
        const txt = tr(
          "Hb 12.4，PLT 92k，AST 168，ALT 142，LDH 620，Cr 0.9，尿酸 7.2",
          "Hb 12.4, PLT 92k, AST 168, ALT 142, LDH 620, Cr 0.9, uric acid 7.2",
        );
        finding(tr("抽血", "Labs"), txt);
        post("nurse", "result", "xiaowen", tr(`抽血結果出來了：${txt}`, `Labs are back: ${txt}`));
      }
      if (k === "nst") {
        finding("NST", tr("反應型，基線 145 bpm，沒有宮縮", "Reactive, baseline 145 bpm, no contractions"));
        post(
          "nurse",
          "result",
          "xiaowen",
          tr("NST：反應型，基線 145，沒有宮縮。", "NST: reactive, baseline 145, no contractions."),
        );
        if (!S.seizing && !S.gone && S.seizureAt === null) {
          S.ptLoc = "wait";
          walkEnt(ent("yijun"), [9, 8]);
        }
      }
      if (k === "rebp") {
        const b = bp();
        S.bpSeen.push({ t, ...b });
        post(
          "nurse",
          "result",
          "xiaowen",
          tr(`重量血壓：${b.sbp}/${b.dbp}，HR ${b.hr}。`, `Repeat BP: ${b.sbp}/${b.dbp}, HR ${b.hr}.`),
        );
      }
    }
    if (!S.accepted && S.seizureAt === null && t >= 40) seize();
    if (S.seizing && S.seizureAt !== null && t >= S.seizureAt + 2 && !S.sz2) {
      S.seizing = false;
      ent("yijun").fx2 = "down";
      clearWait("sz");
      post(
        "pt",
        "narr",
        null,
        tr(
          "抽搐停了。怡君呼吸很重，叫她只會含糊地哼，嘴角有一點血（咬到舌頭）。",
          "The seizure stops. Yi-Chun is breathing heavily and only mumbles when you call her name. There’s a little blood at the corner of her mouth (she bit her tongue).",
        ),
      );
    }
    if (!S.helpCalled && S.seizureAt !== null && t === S.seizureAt + 2) {
      S.helpCalled = true;
      helpArrives(tr("阿嬤的尖叫聲把小雯引來了。", "Grandma’s screaming brought Hsiao-Wen running."));
    }
    if (S.seizure2At === t && !S.mg) {
      S.seizing = true;
      S.sz2 = true;
      ent("yijun").fx2 = "shake";
      post("pt", "alert", "xiaowen", tr("她又抽了！", "She’s seizing again!"));
      eventBanner(tr("再次抽搐", "Second seizure"), tr("還沒有上 MgSO4", "No MgSO4 yet"), true);
    }
    if (S.sz2 && S.seizure2At !== null && t === S.seizure2At + 2) {
      S.seizing = false;
      S.sz2 = false;
      ent("yijun").fx2 = "down";
    }
    if (S.vsComing === t) {
      const pt = ent("yijun");
      walkEnt(ent("vs"), [pt.x + 1 <= 18 ? pt.x + 1 : pt.x - 1, pt.y]);
      post(
        "pt",
        "npc",
        "vs",
        S.mg
          ? tr("Mg 上了嗎？好。推床，送產房，通知麻醉。", "Mg running? Good. Gurney, take her to L&D, call anesthesia.")
          : tr("MgSO4 4 克 IV loading，現在！推床送產房。", "MgSO4 4 g IV loading, now! Gurney, to L&D."),
      );
      S.mg = true;
      S.endAt = t + 3;
    }
    if (S.endAt === t && S.seizureAt !== null) endB();
  },
  timeup: () =>
    void playEnding(
      "Z",
      tr("交班", "Sign-out"),
      [
        {
          who: "xiaowen",
          text: tr(
            "學弟，主任說 3 號他自己來，你先去吃飯。",
            "Dr. Lin says he’ll take number 3 himself. Go get lunch.",
          ),
        },
      ],
      tr("時間到了，主任接手。", "Time’s up. Dr. Lin takes over."),
    ),

  hud() {
    const lastBp = S.bpSeen.at(-1);
    const dip = S.orders.dip?.done ? "3+" : tr("還沒驗", "Not done");
    return [
      [
        lastBp ? tr(`${clock(lastBp.t)} 你量的血壓`, `Your BP at ${clock(lastBp.t)}`) : tr("你量的血壓", "Your BP"),
        lastBp
          ? `${lastBp.sbp}/${lastBp.dbp}`
          : S.printRead
            ? tr("分診 158/104", "Triage 158/104")
            : tr("還沒量", "Not taken"),
      ],
      [tr("尿蛋白", "Urine protein"), dip],
    ];
  },
  urgent: () => S.seizureAt !== null,
  outcomeText: () =>
    S.seizureAt !== null
      ? tr("門診抽搐後送產房", "Seized in clinic, sent to L&D")
      : tr("主任收住院", "Admitted by Dr. Lin"),

  graderCase: tr(
    `病例：30 歲初產婦，34 週例行產檢。真相：子癲前症合併嚴重特徵，正往 HELLP 走（血壓 160/110 以上、頭痛、視力閃光、上腹痛、嘔吐、血小板 92k、肝指數上升）。病人自己淡化症狀、不想住院；先生和候診的阿嬤知道一些病人沒說的事。病人會問能不能先去吃東西：讓她離開會延誤評估，而且她之後若要緊急生產就不是空腹；好的 clerk 會請她先留下、暫時禁食。分診的自動血壓計印出 158/104 但沒人標註。若 40 分鐘內沒有讓主任收住院、上 MgSO4，病人會在門診發生子癲癇抽搐。抽搐時正確處置：側躺保護呼吸道、叫人、給氧、MgSO4 4 g IV loading；錯誤處置：塞東西進嘴巴、壓制四肢。`,
    `Case: 30-year-old primigravida at 34 weeks for a routine prenatal visit. Truth: preeclampsia with severe features, progressing toward HELLP (BP 160/110 or higher, headache, flashing lights in her vision, epigastric pain, vomiting, platelets 92k, rising liver enzymes). The patient downplays her symptoms and doesn't want to be admitted; her husband and a grandma in the waiting area know things the patient hasn't said. The patient will ask whether she can go eat first: letting her leave delays the workup, and she would no longer be NPO if she later needs an urgent delivery; a good clerk asks her to stay and keeps her NPO for now. The automated BP machine at triage printed 158/104 but nobody flagged it. If the attending has not admitted her and started MgSO4 within 40 minutes, the patient has an eclamptic seizure in clinic. Correct seizure management: turn her on her side to protect the airway, call for help, give oxygen, MgSO4 4 g IV loading; wrong management: putting something in her mouth, restraining her limbs.`,
  ),
  rubric: tr(
    [
      "血壓：注意到嚴重範圍血壓並重量確認",
      "問出嚴重特徵：頭痛、視力、上腹痛、嘔吐，包含從先生或阿嬤取得的資訊",
      "身體檢查：水腫、反射與 clonus、上腹壓痛、胎心",
      "檢查：尿蛋白、HELLP 相關抽血、NST",
      "向主治報告與處置建議（收住院、MgSO4、降壓、評估生產時機）",
      "抽搐的預防或處置",
      "與淡化症狀、不想住院的病人溝通",
    ],
    [
      "BP: noticed the severe-range BP and rechecked it",
      "Elicited severe features: headache, vision, epigastric pain, vomiting, including information from the husband or the grandma",
      "Physical exam: edema, reflexes and clonus, epigastric tenderness, fetal heart",
      "Workup: urine protein, HELLP labs, NST",
      "Presentation to the attending and proposed management (admit, MgSO4, BP control, assess timing of delivery)",
      "Seizure prevention or management",
      "Communication with a patient who downplays her symptoms and doesn't want to be admitted",
    ],
  ),
  facts: () => [
    tr(
      `讓病人離開去吃東西：${S.awayAt !== null ? `是（${clock(S.awayAt)}）` : "否"}${S.toldStay ? "；有請她留下、先別吃" : ""}`,
      `Let the patient leave to eat: ${S.awayAt !== null ? `yes (${clock(S.awayAt)})` : "no"}${S.toldStay ? "; asked her to stay and not eat yet" : ""}`,
    ),
    tr(`分診血壓印單：${S.printRead ? "看過" : "沒看"}`, `Triage BP printout: ${S.printRead ? "read" : "not read"}`),
    tr(`自己量血壓：${S.bpSeen.length} 次`, `BPs taken personally: ${S.bpSeen.length}`),
    tr(`問出的病史：${S.revealed.size} 項`, `History items obtained: ${S.revealed.size}`),
    tr(`尿蛋白：${S.orders.dip ? "有驗" : "沒驗"}`, `Urine protein: ${S.orders.dip ? "checked" : "not checked"}`),
    tr(`抽血：${S.orders.labs ? "有" : "沒有"}`, `Labs: ${S.orders.labs ? "yes" : "no"}`),
    tr(
      `主任收住院：${S.accepted && S.acceptedAt !== null ? clock(S.acceptedAt) : "沒有"}`,
      `Admitted by attending: ${S.accepted && S.acceptedAt !== null ? clock(S.acceptedAt) : "no"}`,
    ),
    tr(
      `抽搐：${S.seizureAt !== null ? `有（${clock(S.seizureAt)}）` : "沒有"}`,
      `Seizure: ${S.seizureAt !== null ? `yes (${clock(S.seizureAt)})` : "no"}`,
    ),
  ],
  known,
};
